import { IntlShape } from 'react-intl';

import { IDocsListItem, TDocStatus } from '@src-common/view/components/DocsList';

import { ISupplementaryDocsGroup } from './types';

const STATUS_PRIORITY: TDocStatus[] = ['rejected', 'expired', 'pending', 'approved'];

const aggregateStatus = (main: IDocsListItem, members: IDocsListItem[]): TDocStatus => {
  if (main.status === 'missing') {
    return 'missing';
  }

  return STATUS_PRIORITY.find(status => members.some(doc => doc.status === status)) ?? main.status;
};

const collapseDocsList = <TDoc extends IDocsListItem>(
  docs: TDoc[],
  groups: Partial<Record<string, ISupplementaryDocsGroup>>,
  intl: IntlShape,
): TDoc[] => {
  const docsByType = new Map(docs.map(doc => [doc.docType, doc]));
  const hiddenDocTypes = new Set<string>();

  Object.entries(groups).forEach(([mainDocType, group]) => {
    if (group && docsByType.has(mainDocType)) {
      group.questions.forEach(({ docType }) => hiddenDocTypes.add(docType));
    }
  });

  return docs
    .filter(doc => !hiddenDocTypes.has(doc.docType))
    .map(doc => {
      const group = groups[doc.docType];

      if (!group) {
        return doc;
      }

      // documents under silent review never change what the driver sees
      const members = [
        doc,
        ...group.questions.flatMap(({ docType, silentReview }) =>
          silentReview ? [] : (docsByType.get(docType) ?? []),
        ),
      ];
      const status = aggregateStatus(doc, members);
      const expiryDates = members.flatMap(member => member.expiresAt ?? []);

      return {
        ...doc,
        title: group.title ? intl.formatMessage(group.title) : doc.title,
        status,
        rejectedReason: members.find(member => member.status === status)?.rejectedReason,
        expiresSoon: members.some(member => member.expiresSoon),
        expiresAt: expiryDates.length
          ? new Date(Math.min(...expiryDates.map(date => date.getTime())))
          : null,
      };
    });
};

export { collapseDocsList };
